import type { AllowedActionable } from 'pkg/api/models/allowed_actionable';
import type { Dateable } from 'pkg/api/models/dateable';
import type { Group } from 'pkg/api/models/group';
import type { Record } from 'pkg/api/models/record';
import type { User } from 'pkg/api/models/user';
import type { UserRating } from 'pkg/api/models/user_rating';

export enum RoleType {
	Unknown = 'unknown',
	Player = 'player',
	Staff = 'staff',
	Admin = 'admin',
	Owner = 'owner',
	ThreeSixtyAdmin = '360admin',
}

export const MinJerseyNumber = 0;
export const MaxJerseyNumber = 999;

export function isValidJerseyNumber(value: string): boolean {
	if (value === '') {
		return true;
	}

	const number = Number(value);

	return (
		Number.isInteger(number) &&
		number >= MinJerseyNumber &&
		number <= MaxJerseyNumber
	);
}

export enum MembershipStatus {
	Pending = 0,
	Active = 1,
	Blocked = 2,
	Inactive = 3,
}

export enum Capabilities {
	Sport = 'sport',
	Chat = 'chat',
	PaymentAdmin = 'payment-admin',
	Forms = 'forms',
	Scheduler = 'scheduler',
	RelationManager = 'relation-manager',
	SiteManagement = 'site-management',
}

export interface Membership extends Record, Dateable, AllowedActionable {
	groupId: number;
	id: number;
	role: RoleType;
	status: number;
	title?: string;
	jerseyNumber?: number | null;
	position?: string | null;
	userId: number;
	lastPerformanceReviewAt?: number;
	capabilities: Capabilities[];
	inactivatedAt?: number;
	// The assigned role's own name, e.g. "Academy coach". Sent only for
	// organizations on custom roles, and only by endpoints that load the role —
	// `role` above is the type it was built from, which is all there is to fall
	// back on.
	roleName?: string;

	isLegalGuardian?: boolean;
	isOrganizationMembership?: boolean;
	// Family mode: a parent viewing all their children at once. Always paired
	// with isOrganizationMembership, and never sets targetUserId, since no single
	// child is targeted. Unrelated to family billing groups.
	isFamilyMembership?: boolean;
	targetUserId?: number;
	isInherited?: boolean;

	user?: User;
	targetUser?: User;
	group?: Group;
	lastRating?: UserRating;
	lastSelfRating?: UserRating;
}

export interface MembershipGroups {
	adminOrStaff: Membership[];
	players: Membership[];
	parents: Membership[];
}
